import React, { useCallback, useEffect, useRef, useState } from "react";

import { Stack } from "@mui/material";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import StarterKit from "@tiptap/starter-kit";
import { AIAnswerText } from "components/Documents/AI-answer-text/AIAnswerText";
import { AiSkeletonLoader } from "components/Documents/AI-skeleton-loader/AISkeletonLoader";
import { ChatInput } from "components/Documents/AI-text-box/ChatInput";
import { useContentContext } from "components/Documents/Content/content-context";
import { DocumentSectionType } from "constants/documents";
import { useActionHook, useDidUpdate } from "hooks";
import { debounce } from "lodash";
import { LinkBubbleMenu, LinkBubbleMenuHandler, RichTextEditor, type RichTextEditorRef } from "mui-tiptap";
import { DocumentSection, DocumentSectionText } from "store/types";

import { InlineMeasureExtension } from "./InlineMeasureExtension";
import { useInlineMeasureTiptap } from "./use-inline-measure";

export interface TextSectionProps {
  section: DocumentSectionText;
  onCreateSection?: (data: {
    question?: string;
    text?: string;
    position?: number;
  }) => Promise<DocumentSection | undefined>;
  onBlur?: (e: React.ChangeEvent) => void;
  onFocus?: () => void;
  documentDealId: number | null;
}

//TODO: when create new section is pressed, hide the AI box

export const TextSection: React.FC<TextSectionProps> = ({
  section,
  onCreateSection,
  // onBlur,
  onFocus,
  documentDealId
}) => {
  const rteRef = useRef<RichTextEditorRef>(null);
  const isAiAnswer = section && section.parameters && section.parameters.question;

  const {
    setCurrentEditor,
    setCurrentSectionId,
    updateSection,
    AIAnswerSection,
    sectionsToOpenChatInput,
    removeSectionFromOpenChatInput,
    addSectionToOpenChatInput,
    sectionsAiRequest,
    removeSectionAiRequest,
    currentSectionId,
    currentSectionIsRtl,
    setCurrentSectionIsRtl
  } = useContentContext();
  const { action: getAIOptionAnswer, loading: isChatAiAnswerLoading } = useActionHook(AIAnswerSection);

  // This section was created to hold an AI answer: it runs the request itself so the
  // "thinking" loader stays here (the source section is left untouched).
  const hasPendingAiRequest = sectionsAiRequest.has(section.id);
  const pendingAiRequestFiredRef = useRef(false);
  const { handleInlineMeasureClick, insertInlineMeasureElement, updateMeasures } = useInlineMeasureTiptap();
  const hasUpdatedMeasuresRef = useRef(false);

  const [showAIBox, setShowAIBox] = useState(false);
  const [displayedAnswer, setDisplayedAnswer] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const AIBoxRef = useRef<HTMLInputElement>(null);

  const updateDebounce = useCallback(
    debounce((html: string, sectionId: number) => {
      updateSection({ id: sectionId, text: html, disableSetState: true });
    }, 1000),
    [updateSection]
  );

  const handleKeyDownAt = () => {
    setTimeout(() => {
      const editor = rteRef.current?.editor;
      if (editor) {
        insertInlineMeasureElement(editor);
      }
    }, 0);
  };

  const startTypingAnimation = (text: string) => {
    setIsTyping(true);
    setDisplayedAnswer("");

    const words = text.split(" ");
    let currentWordIndex = 0;
    let accumulator = "";

    const timer = setInterval(() => {
      if (currentWordIndex < words.length) {
        accumulator += (currentWordIndex === 0 ? "" : " ") + words[currentWordIndex];
        setDisplayedAnswer(accumulator);
        currentWordIndex++;
      } else {
        clearInterval(timer);
        setIsTyping(false);
        setTimeout(() => {
          AIBoxRef.current?.focus();
        }, 100);
      }
    }, 45);

    return () => clearInterval(timer);
  };

  // Update inline measures with fresh data once at first render when editor is ready
  useEffect(() => {
    if (showAIBox || isAiAnswer || isChatAiAnswerLoading) return;
    if (hasUpdatedMeasuresRef.current) return;

    const timer = setTimeout(() => {
      const editor = rteRef.current?.editor;
      if (editor) {
        hasUpdatedMeasuresRef.current = true;
        updateMeasures(editor);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [showAIBox, isAiAnswer, isChatAiAnswerLoading, updateMeasures]);

  // Sync editor when section.text changes from external source (WebSocket) - only when it differs from what we sent
  useDidUpdate(() => {
    const editor = rteRef.current?.editor;
    if (!editor || showAIBox || isAiAnswer || isChatAiAnswerLoading) return;

    const incomingText = section?.text ?? "";
    editor.commands.setContent(incomingText, { emitUpdate: false });
  }, [section?.text]);

  // Sync text direction when section.is_rtl changes and this is the current section
  useEffect(() => {
    if (currentSectionId === section.id && section.is_rtl !== undefined) {
      setCurrentSectionIsRtl(section.is_rtl);
    }
  }, [section.is_rtl, section.id, currentSectionId, setCurrentSectionIsRtl]);

  // Register editor update handler - syncs to global state, skips when applying external update
  useEffect(() => {
    const editor = rteRef.current?.editor;
    if (!editor) return;

    const handleUpdate = (e: { editor: { getText: () => string; getHTML: () => string } }) => {
      const text = e.editor.getText();
      if (text === " ") {
        setShowAIBox(true);
        setTimeout(() => {
          AIBoxRef.current?.focus();
        }, 100);
        return;
      }

      updateDebounce(e.editor.getHTML(), section.id);
    };

    editor.on("update", handleUpdate);
    return () => {
      editor.off("update", handleUpdate);
    };
  }, [section.id, updateDebounce]);

  useEffect(() => {
    // This section was created to hold an AI answer — run the request from here so the loader
    // renders in place, then add a fresh empty section with an open ChatInput for further input.
    const request = sectionsAiRequest.get(section.id);
    if (!request || pendingAiRequestFiredRef.current) return;

    pendingAiRequestFiredRef.current = true;

    const runPendingAiRequest = async () => {
      await getAIOptionAnswer({
        ...request,
        documentId: section.document_id,
        sectionId: section.id
      });

      removeSectionAiRequest(section.id);

      if (onCreateSection) {
        const inputSection = await onCreateSection({ question: "", text: "" });
        if (inputSection && inputSection.type === DocumentSectionType.Text) {
          addSectionToOpenChatInput(inputSection.id);
        }
      }
    };

    runPendingAiRequest();
  }, [
    section.id,
    section.document_id,
    sectionsAiRequest,
    getAIOptionAnswer,
    removeSectionAiRequest,
    onCreateSection,
    addSectionToOpenChatInput
  ]);

  useEffect(() => {
    // Check if this section should open ChatInput (marked when creating new section after selectedOption)
    if (section?.id && sectionsToOpenChatInput.has(section.id)) {
      setShowAIBox(true);
      removeSectionFromOpenChatInput(section.id);
      // Focus the section and the ChatInput input
      onFocus?.();
      // Focus the ChatInput input field after a short delay to ensure it's rendered
      setTimeout(() => {
        if (AIBoxRef.current) {
          AIBoxRef.current.focus();
        }
      }, 100);
    }
  }, [section?.id, sectionsToOpenChatInput, removeSectionFromOpenChatInput, onFocus]);

  useEffect(() => {
    const isRecentlyCreated =
      section?.created_at && new Date().getTime() - new Date(section.created_at).getTime() < 5000; // 5 seconds

    if (!section?.text) return;

    if (isRecentlyCreated) {
      startTypingAnimation(section.text);
      section.text && section.parameters?.question && setShowAIBox(true);
    }
    if (section?.text && !isTyping) {
      setDisplayedAnswer(section.text);
    }
  }, [section?.text, section?.parameters?.question, section?.created_at, isTyping]);

  return (
    <Stack width="100%" sx={{ height: "auto" }}>
      {isAiAnswer && (
        <AIAnswerText question={section.parameters?.question || ""} answer={displayedAnswer} isTyping={isTyping} />
      )}
      {hasPendingAiRequest && !isAiAnswer && !isChatAiAnswerLoading && <AiSkeletonLoader />}
      {!showAIBox && !isAiAnswer && !isChatAiAnswerLoading && !hasPendingAiRequest && (
        <RichTextEditor
          ref={rteRef}
          RichTextFieldProps={{ variant: "standard" }}
          sx={editorSx}
          onFocus={() => {
            setCurrentEditor(rteRef.current?.editor || null);
            setCurrentSectionId(section.id);
            setCurrentSectionIsRtl(section.is_rtl ?? false);
          }}
          editorProps={{
            handleKeyDown: (_view, event) => {
              if (event.key === "@") {
                handleKeyDownAt();
              }
            }
          }}
          extensions={[
            InlineMeasureExtension.configure({ onClick: handleInlineMeasureClick }),
            StarterKit,
            Placeholder.configure({
              placeholder:
                section?.id != null ? "Start writing a text or press 'space' for AI commands" : "Start writing a text",
              showOnlyCurrent: true // Only show in the focused empty paragraph (not in other empty paragraphs)
            }),
            TextAlign.configure({
              types: ["heading", "paragraph", "left", "center", "right", "justify"] // nodes that support alignment
            }),
            LinkBubbleMenuHandler
          ]}
          textDirection={
            currentSectionId === section.id && currentSectionIsRtl !== null
              ? currentSectionIsRtl
                ? "rtl"
                : "ltr"
              : section?.is_rtl
                ? "rtl"
                : "ltr"
          }
          content={section?.text || ""}
        >
          {() => (
            <>
              <LinkBubbleMenu />
            </>
          )}
        </RichTextEditor>
      )}
      <ChatInput
        section={section}
        open={showAIBox}
        setOpen={setShowAIBox}
        inputRef={AIBoxRef}
        onCreateSection={onCreateSection}
        isTyping={isTyping}
        startTyping={startTypingAnimation}
        documentDealId={documentDealId}
        isChatAiAnswerLoading={isChatAiAnswerLoading}
      />
    </Stack>
  );
};

const editorSx = {
  width: "100%",
  "@keyframes skeleton-loading": {
    to: {
      backgroundPosition: "left"
    }
  },

  "& .MuiTiptap-RichTextField-content": {
    padding: 0
  },
  "& .MuiBox-root": {
    border: "none !important",
    boxShadow: "none !important"
  },
  "& .tiptap": {
    color: "#071F35",
    padding: 0,
    outline: "none",
    minHeight: 24,
    "& p": {
      lineHeight: 1.5,
      margin: 0,
      marginBottom: "10px"
    },
    "& p:last-child": {
      marginBottom: 0
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      lineHeight: 1.4,
      margin: 0,
      marginBottom: "10px"
    },
    "& h1:last-child, & h2:last-child, & h3:last-child, & h4:last-child, & h5:last-child, & h6:last-child": {
      marginBottom: 0
    },
    "& ul, & ol": {
      paddingLeft: "1.5em"
    },
    "& li": {
      marginLeft: "18px"
    },
    "& a:not([data-type='mention'])": {
      color: "#26A6AE !important",
      "&:hover": {
        textDecoration: "underline"
      }
    },
    // Placeholder only for the first line
    // Placeholder only in the focused empty paragraph: when doc is empty (first p) or when user switched to a new empty row
    // Previous: always visible placeholder — "& p.is-empty::before": { color: "#9eacb5", content: "attr(data-placeholder)", float: "left", height: 0, pointerEvents: "none" }
    "& p.is-editor-empty:first-of-type::before": {
      content: "attr(data-placeholder)",
      float: "left",
      height: 0,
      pointerEvents: "none",
      color: "transparent"
    },
    "&:focus-within p.is-editor-empty:first-of-type::before": {
      color: "#9eacb5"
    },
    "& .inline-measure": {
      position: "relative",
      display: "inline-block",
      backgroundColor: "#ecf1f8",
      padding: "0 2px",
      borderRadius: 4,
      fontSize: 14,
      margin: "0 2px",
      verticalAlign: "middle",
      cursor: "pointer",
      userSelect: "none",
      pointerEvents: "auto"
    },
    "& .inline-measure-skeleton": {
      minWidth: 50,
      minHeight: 20,
      cursor: "progress",
      background: "linear-gradient(90deg, #ddda 40%, #efefefaa, #ddda 60%) right / 300% 100%",
      animation: "skeleton-loading 1.5s linear infinite"
    }
  }
};
